import {
  GoAButton,
  GoAFormItem,
  GoARadioGroup,
  GoARadioItem,
  GoASpacer,
} from '@abgov/react-components';
import { BaseWizardProps } from '../wizard/wizard-step-base';
import {
  defaultValidate,
  WizardButtonGroup,
} from '../wizard/wizard-button-group-component';
import { useEffect, useRef, useState } from 'react';
import { useIsDesktop } from '../../utils/isDesktop';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../state/store';
import {
  DamageSketch,
  DesktopDamageSelectorLayoutConfiguration,
  MobileDamageSelectorLayoutConfiguration,
} from '../sketches/damage';
import { InitialPointOfImpact } from '../../models/initial-point-of-impact';
import p5 from 'p5';
import {
  pushNotification,
  removeAllValidationErrorNotifications,
} from '../../state/slices/notificationSlice';
import { DamageCardComponent } from './damage-card';
import {
  updateCollision,
  validateDriver,
} from '../../state/slices/collisionSlice';
import { useNavigate } from 'react-router-dom';
import { Collision, DamageSelection } from '../../models/collision';
import { NotificationType } from '../notifications/notification-component';
import {
  getReturnToValue,
  IsInReviewEditMode,
} from '../wizard/wizard-component';
import {
  setReviewDamageModified,
  setReviewPointOfImpactModified,
} from '../../state/slices/configurationSlice';

export interface FirstPointOfImpactComponentProps extends BaseWizardProps {}

const useAppDispatch = () => useDispatch<AppDispatch>();
const useAppSelector: <TSelected>(
  selector: (state: RootState) => TSelected,
) => TSelected = useSelector;

export const FirstPointOfImpactComponent = ({
  navigationConfiguration,
}: FirstPointOfImpactComponentProps) => {
  const isDesktop = useIsDesktop();
  const defaultSelection = '0';

  const errorText = 'Choose one an option that best applies to your situation';
  const dispatch = useAppDispatch();

  const stateCollision = useAppSelector((state: RootState) => state.collision);
  const stateConfiguration = useAppSelector(
    (state: RootState) => state.configuration,
  );

  const [nextPage, setNextPage] = useState('');

  const [damageSketch, setDamageSketch] = useState<DamageSketch>(
    new DamageSketch(),
  );

  const [
    initialPointOfImpactLocationList,
    setInitialPointOfImpactLocationList,
  ] = useState<InitialPointOfImpact[]>([]);

  const [
    releventPointOfImpactLocationList,
    setReleventPointOfImpactLocationList,
  ] = useState<InitialPointOfImpact[]>([]);

  const [damageSelectionsList, setDamageSelectionsList] = useState<
    DamageSelection[]
  >(stateCollision.collision.vehicles![0].damage?.damageSelection!);

  const [formError, setFormError] = useState('');
  const [selection, setSelection] = useState(defaultSelection);

  const p5Ref = useRef(null);
  const containerDiv = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setNextPage(
      navigationConfiguration.pagingConfiguration.find(
        (item) => item.default !== undefined,
      )?.default ?? 'error',
    );
  }, []);

  useEffect(() => {
    stateCollision.collision.vehicles![0].damage!.damageSelection!.map(
      (x: any) => {
        if (x.isInitialPointOfImpact) {
          setSelection(x.pointOfImpactId!.toString());
        }
      },
    );
  }, [stateCollision.collision.vehicles![0]]);

  useEffect(() => {
    const damageSelectionsListValues = damageSelectionsList.map((item) =>
      item.pointOfImpactId?.toString(),
    );

    const releventDamageLocations = initialPointOfImpactLocationList.filter(
      (item) => damageSelectionsListValues.includes(item.id.toString()),
    );

    setReleventPointOfImpactLocationList(releventDamageLocations);
  }, [initialPointOfImpactLocationList, damageSelectionsList]);

  useEffect(() => {
    const pois: InitialPointOfImpact[] = [];

    stateConfiguration.configuration.pointOfImpact.forEach((poi: any) => {
      pois.push(new InitialPointOfImpact(poi.id, poi.value));
    });

    setInitialPointOfImpactLocationList(pois);
  }, [stateConfiguration.configuration.pointOfImpact]);

  useEffect(() => {
    if (p5Ref.current) {
      let startCount = 1;

      const p5Instance = new p5(
        damageSketch.sketch(
          containerDiv.current!,
          stateCollision.collision.vehicles![0].damage!.damageSelection!,
          releventPointOfImpactLocationList,
          startCount,
          () => {},
          false,
          isDesktop
            ? new DesktopDamageSelectorLayoutConfiguration(
                releventPointOfImpactLocationList,
              )
            : new MobileDamageSelectorLayoutConfiguration(
                releventPointOfImpactLocationList,
              ),
        ),
        p5Ref.current,
      );

      return () => {
        p5Instance.remove();
      };
    }
  }, [releventPointOfImpactLocationList]);

  const validate = (): boolean => {
    if (!selection || selection === defaultSelection) {
      setFormError(errorText);
      dispatch(
        pushNotification({
          body: errorText,
          notificationType: NotificationType.importantValidationError,
        }),
      );
      return false;
    }

    return true;
  };
  const save = () => {
    let updatedCollision: Collision = { ...stateCollision.collision };

    if (
      navigationConfiguration.updateMetadata &&
      navigationConfiguration.stepId
    ) {
      const stepUpdated = navigationConfiguration.updateMetadata(
        updatedCollision,
        navigationConfiguration.stepId!,
        navigationConfiguration.stageCompleted
          ? navigationConfiguration.stageCompleted
          : false,
        !navigationConfiguration.stageCompleted,
      );

      if (stepUpdated && updatedCollision.metadata) {
        updatedCollision.metadata = {
          ...updatedCollision.metadata,
          formState: stepUpdated,
        };
      }
    }

    const damageList =
      stateCollision.collision.vehicles![0].damage!.damageSelection!.map(
        (item: any) => {
          return item.pointOfImpactId.toString() === selection
            ? { ...item, isInitialPointOfImpact: true }
            : { ...item, isInitialPointOfImpact: false };
        },
      );

    updatedCollision.vehicles = [
      {
        ...stateCollision?.collision?.vehicles?.[0],
        damage: {
          ...stateCollision?.collision?.vehicles?.[0]?.damage,
          damageSelection: [...damageList],
        },
      },
    ];

    dispatch(updateCollision(updatedCollision));

    if (navigationConfiguration.save) {
      navigationConfiguration.save();
    }
  };

  return (
    <div>
      <h1>Vehicle damage</h1>
      Please provide the required information about the damage to your vehicle.
      <GoASpacer vSpacing={'l'} />
      <h2>Indicate your first point of damage</h2>
      <GoASpacer vSpacing={'m'} />
      Please identify the area of your vehicle that was damaged first in the
      collision (the first point of impact). You can only make one selection. If
      you're unsure which point of damage occurred first, select the one you
      believe to be first, to the best of your ability.
      <div ref={containerDiv} className="damage-vehicle">
        <div ref={p5Ref} />
      </div>
      <GoASpacer vSpacing={'m'} />
      <GoAFormItem
        label="Select one first point of damage below"
        error={formError}
      >
        <GoARadioGroup
          name="first-point-of-impact"
          value={selection}
          onChange={(item, value) => {
            if (IsInReviewEditMode()) {
              dispatch(setReviewPointOfImpactModified(true));
            }

            setSelection(value);
            setFormError('');
            dispatch(removeAllValidationErrorNotifications());
          }}
          error={formError.length > 0}
        >
          {damageSelectionsList.map((item, index) => {
            return (
              <GoARadioItem
                key={index}
                value={stateConfiguration.configuration.pointOfImpact
                  .find((x: any) => x.id === parseInt(item.pointOfImpactId))
                  ?.id.toString()}
                label={`${index + 1}. ${
                  stateConfiguration.configuration.pointOfImpact.find(
                    (x: any) => x.id === parseInt(item.pointOfImpactId),
                  )?.formLabel
                }`}
              />
            );
          })}
        </GoARadioGroup>
      </GoAFormItem>
      <GoASpacer vSpacing={'2xl'} />
      <div>
        <WizardButtonGroup
          nextPage={nextPage}
          save={save}
          validate={(): boolean => {
            return validate();
          }}
          goToReview={navigationConfiguration.goToReview}
          navigate={navigationConfiguration.navigate}
          showViewSummaryButton={!IsInReviewEditMode()}
        />
      </div>
    </div>
  );
};
